import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../store/authStore';
import { useSupabaseAuth } from '../../hooks/useSupabaseAuth';
import { AlertCircle, Eye, EyeOff, Loader2, ArrowLeft } from 'lucide-react';
import { PasswordStrengthMeter } from '../../components/auth/PasswordStrengthMeter';
import { AuthLayout } from '../../components/auth/AuthLayout';
import { OAuthButtonRow } from '../../components/auth/OAuthButtonRow';
import styles from './auth-form.module.css';

export function Register() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { register, isLoading, error, clearError, isAuthenticated } = useAuthStore();
  const { signInWithGoogle, signInWithTwitter, signInWithDiscord, loading: oauthLoading } = useSupabaseAuth();

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [localError, setLocalError] = useState('');
  // zxcvbn score gate from <PasswordStrengthMeter>. Replaces the previous
  // Latin-only regex (/[A-Z]/, /[0-9]/) which silently rejected strong
  // non-Latin passphrases (Cyrillic, Devanagari, CJK). The meter's
  // callback runs from a useEffect so calling setState here is safe.
  const [meterStrong, setMeterStrong] = useState(false);
  const handleStrengthChange = useCallback((_score: number, isValid: boolean) => {
    setMeterStrong(isValid);
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  // Clear error on unmount
  useEffect(() => {
    return () => clearError();
  }, [clearError]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setLocalError(t('auth.passwordMismatch', 'Passwords do not match'));
      return;
    }

    // Length gate — universal, no script-specific rules. The remaining
    // strength check (entropy, dictionary words, etc.) is delegated to
    // the zxcvbn-based <PasswordStrengthMeter> + `meterStrong` state.
    if (password.length < 8) {
      setLocalError(t('auth.passwordTooShort', 'Password must be at least 8 characters'));
      return;
    }
    if (!meterStrong) {
      setLocalError(
        t('auth.passwordTooWeak', {
          defaultValue:
            "Le mot de passe n'est pas assez fort. Utilise un mélange de lettres, chiffres et symboles.",
        }),
      );
      return;
    }

    try {
      await register(email, password, username || undefined);
      // Navigation handled by useEffect above
    } catch (error) {
      // Error handled by store
    }
  };

  return (
    <AuthLayout>
        {/* Header */}
        <div className={styles.header}>
          <h1 className={styles.title}>
            {t('auth.createAccount', 'Create Account')}
          </h1>
          <p className={styles.subtitle}>
            {t('auth.registerSubtitle', 'Get started with cloud sync for your wallets')}
          </p>
        </div>

        {/* Error Message */}
        {(error || localError) && (
          <div role="alert" className={styles.errorBanner}>
            <AlertCircle size={16} color="var(--error)" />
            <span className={styles.errorText}>
              {localError || error}
            </span>
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit}>
          {/* Email */}
          <div className={styles.field}>
            <label className={styles.label}>
              {t('auth.email', 'Email')} *
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className={styles.input}
            />
          </div>

          {/* Username */}
          <div className={styles.field}>
            <label className={styles.label}>
              {t('auth.username', 'Username')} ({t('auth.optional', 'optional')})
            </label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="johndoe"
              className={styles.input}
            />
          </div>

          {/* Password */}
          <div className={styles.field}>
            <label className={styles.label}>
              {t('auth.password', 'Password')} *
            </label>
            <div className={styles.passwordWrapper}>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
                className={styles.inputWithIcon}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className={styles.passwordToggle}
                aria-label={
                  showPassword
                    ? t('auth.hidePassword', { defaultValue: 'Hide password' })
                    : t('auth.showPassword', { defaultValue: 'Show password' })
                }
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
            {/* zxcvbn-powered live strength meter. Score ≥ 3 (default
                minScore) sets `meterStrong` true and unlocks submission.
                Replaces the Latin-only regex hint that lived here before. */}
            <div className={styles.passwordMeterSlot}>
              <PasswordStrengthMeter password={password} onStrengthChange={handleStrengthChange} />
            </div>
            {!password && (
              <p className={styles.passwordHint}>
                {t('auth.passwordHint', {
                  defaultValue:
                    'Au moins 8 caractères. Évite les mots du dictionnaire.',
                })}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div className={styles.fieldTight}>
            <label className={styles.label}>
              {t('auth.confirmPassword', 'Confirm Password')} *
            </label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              placeholder="••••••••"
              className={styles.input}
            />
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className={
              isLoading
                ? `${styles.submitButton} ${styles.submitButtonDisabled}`
                : styles.submitButton
            }
          >
            {isLoading ? (
              <>
                <Loader2 size={18} className={styles.spinner} />
                {t('auth.registering', 'Creating Account...')}
              </>
            ) : (
              t('auth.createAccount', 'Create Account')
            )}
          </button>
        </form>

        {/* Divider */}
        <div className={styles.divider}>
          <div className={styles.dividerLine} />
          <span className={styles.dividerLabel}>
            {t('auth.orContinueWith', 'or continue with')}
          </span>
          <div className={styles.dividerLine} />
        </div>

        {/* Social Login Buttons */}
        <OAuthButtonRow
          mode="signup"
          onGoogle={signInWithGoogle}
          onTwitter={signInWithTwitter}
          onDiscord={signInWithDiscord}
          loading={oauthLoading}
        />

        {/* Sign In Link */}
        <div className={styles.signupRow}>
          <span className={styles.signupHint}>
            {t('auth.hasAccount', 'Already have an account?')}{' '}
          </span>
          <Link to="/auth/login" className={styles.signupLink}>
            {t('auth.actions.login', 'Sign In')}
          </Link>
        </div>

        {/* Back to Home */}
        <div className={styles.backRow}>
          <Link to="/" className={styles.backLink}>
            <ArrowLeft size={14} />
            {t('auth.backToHome', 'Back to Home')}
          </Link>
        </div>
    </AuthLayout>
  );
}
